/**
 * ⌘K / Ctrl+K — jump anywhere without touching the mouse.
 *
 * Merdan's job is triage: he lands on the dashboard, sees "3 urgent", and needs to be in the
 * pricing queue. The palette makes that one gesture instead of a mouse trip to the rail, and it
 * carries the LIVE counters — so the jump list is also a second, always-reachable read of what is
 * on fire. The queue-with-work entries sort to the top and the alarming ones sort above those,
 * which means the first thing under the cursor when the palette opens is the most urgent thing in
 * the system.
 *
 * Hand-rolled rather than cmdk-shaped: it is one list with a filter, it needs no portal library,
 * and this app pays for every kilobyte on a connection in Ashgabat.
 *
 * Deliberately NOT a search over orders/users — that would be a request per keystroke against a
 * server this app is careful with. It navigates; the destination screens have their own search.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  AlertOctagon,
  BellRing,
  ClipboardList,
  CornerDownLeft,
  EyeOff,
  FolderTree,
  LayoutDashboard,
  ScrollText,
  Search,
  Settings as SettingsIcon,
  ShieldCheck,
  ShoppingCart,
  Tag,
  UserCheck,
  UserRound,
  Users,
} from 'lucide-react';
import { cn } from '@most/ui';
import type { ComponentType } from 'react';
import { useDashboard } from '@/lib/queries';

interface Command {
  id: string;
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  group: 'actions' | 'nav';
  /** Live count, if this destination has a queue behind it. */
  count?: number;
  /** The count means "broken", not merely "waiting". */
  alarming?: boolean;
}

/** Case- and locale-insensitive substring match. Cyrillic needs the locale-aware lower-casing. */
function matches(haystack: string, needle: string): boolean {
  return haystack.toLocaleLowerCase().includes(needle.toLocaleLowerCase());
}

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useDashboard();

  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const commands = useMemo<Command[]>(() => {
    const urgent = data?.urgent_products ?? 0;
    const dead = data?.dead_notifications ?? 0;
    const newOrders = data?.new_orders ?? 0;
    const approvals = data?.pending_approvals ?? 0;

    // The "actions" group is the queues that currently have something in them. An empty queue is
    // not an action — it stays available below, as plain navigation.
    const actions: Command[] = [];
    if (urgent > 0) {
      actions.push({
        id: 'act-urgent',
        to: '/pricing',
        label: t('palette.actions.urgentPricing'),
        icon: EyeOff,
        group: 'actions',
        count: urgent,
        alarming: true,
      });
    }
    if (dead > 0) {
      actions.push({
        id: 'act-dead',
        to: '/notifications?status=dead',
        label: t('palette.actions.deadNotifications'),
        icon: AlertOctagon,
        group: 'actions',
        count: dead,
        alarming: true,
      });
    }
    if (newOrders > 0) {
      actions.push({
        id: 'act-orders',
        to: '/orders?status=new',
        label: t('palette.actions.newOrders'),
        icon: ShoppingCart,
        group: 'actions',
        count: newOrders,
      });
    }
    if (approvals > 0) {
      actions.push({
        id: 'act-approvals',
        to: '/approvals',
        label: t('palette.actions.pendingApprovals'),
        icon: UserCheck,
        group: 'actions',
        count: approvals,
      });
    }

    const nav: Command[] = [
      { id: 'nav-dash', to: '/', label: t('nav.dashboard'), icon: LayoutDashboard, group: 'nav' },
      {
        id: 'nav-approvals',
        to: '/approvals',
        label: t('nav.approvals'),
        icon: UserCheck,
        group: 'nav',
        count: approvals,
      },
      {
        id: 'nav-pricing',
        to: '/pricing',
        label: t('nav.pricing'),
        icon: Tag,
        group: 'nav',
        count: data?.unpriced_products,
        alarming: urgent > 0,
      },
      {
        id: 'nav-orders',
        to: '/orders',
        label: t('nav.orders'),
        icon: ShoppingCart,
        group: 'nav',
        count: newOrders,
      },
      { id: 'nav-users', to: '/users', label: t('nav.users'), icon: Users, group: 'nav' },
      {
        id: 'nav-categories',
        to: '/categories',
        label: t('nav.categories'),
        icon: FolderTree,
        group: 'nav',
      },
      {
        id: 'nav-notifications',
        to: '/notifications',
        label: t('nav.notifications'),
        icon: dead > 0 ? BellRing : ClipboardList,
        group: 'nav',
        count: dead || data?.failed_notifications,
        alarming: dead > 0,
      },
      { id: 'nav-settings', to: '/settings', label: t('nav.settings'), icon: SettingsIcon, group: 'nav' },
      {
        id: 'nav-monitoring',
        to: '/settings/monitoring',
        label: t('settings.monitoring.title'),
        icon: Activity,
        group: 'nav',
      },
      {
        id: 'nav-roles',
        to: '/settings/roles',
        label: t('settings.roles.title'),
        icon: ShieldCheck,
        group: 'nav',
      },
      {
        id: 'nav-audit',
        to: '/settings/audit',
        label: t('audit.title'),
        icon: ScrollText,
        group: 'nav',
      },
      {
        id: 'nav-profile',
        to: '/profile',
        label: t('nav.profile'),
        icon: UserRound,
        group: 'nav',
      },
    ];

    return [...actions, ...nav];
  }, [data, t]);

  const results = useMemo(() => {
    const q = query.trim();
    if (!q) return commands;
    return commands.filter((c) => matches(c.label, q));
  }, [commands, query]);

  // Reset per opening, and whenever the filter changes the highlight must come back to the top —
  // otherwise the cursor sits on row 5 of a list that now has two rows.
  useEffect(() => {
    setActive(0);
  }, [query, open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      // The input mounts with the dialog; focus after paint.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  if (!open) return null;

  const go = (cmd: Command | undefined) => {
    if (!cmd) return;
    onOpenChange(false);
    navigate(cmd.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (results.length ? (i + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onOpenChange(false);
    }
  };

  // Group headings are rendered by watching the group change as we walk the flat result list —
  // which keeps `active` a single index into one array. Two arrays would mean two-dimensional
  // arrow-key arithmetic for no gain.
  let lastGroup: Command['group'] | null = null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh]"
      role="presentation"
    >
      {/*
        * The scrim is what closes the palette. The handler used to sit on the wrapper above and
        * test `target === currentTarget`, but this scrim covers the whole wrapper, so the click
        * always landed HERE and the test never passed — a click outside did nothing. It is
        * `mousedown`, not `click`, so a drag that starts inside the panel and ends out here
        * (selecting the query text) does not dismiss it.
        */}
      <div
        className="fixed inset-0 animate-fade-in bg-black/60 backdrop-blur-[2px]"
        aria-hidden="true"
        onMouseDown={() => onOpenChange(false)}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('palette.open')}
        onKeyDown={onKeyDown}
        className="relative w-full max-w-xl animate-scale-in overflow-hidden rounded border border-border bg-elevated shadow-overlay"
      >
        <div className="flex items-center gap-2.5 border-b border-border px-4">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('palette.placeholder')}
            aria-label={t('palette.placeholder')}
            /*
             * The global `:focus-visible` ring is right for a field sitting on a page; here it
             * boxes the whole search row of a modal that is, by definition, already focused. The
             * caret and the open panel are the affordance.
             */
            className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          <kbd className="hidden shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-2xs text-muted-foreground sm:block">
            ESC
          </kbd>
        </div>

        <div ref={listRef} className="max-h-[22rem] overflow-y-auto p-1.5">
          {results.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">
              {t('palette.empty')}
            </p>
          ) : (
            results.map((cmd, i) => {
              const Icon = cmd.icon;
              const isActive = i === active;
              const heading = cmd.group !== lastGroup ? cmd.group : null;
              lastGroup = cmd.group;

              return (
                <div key={cmd.id}>
                  {heading ? (
                    <p className="px-2.5 pb-1 pt-3 text-2xs font-semibold uppercase tracking-widest text-muted-foreground/60">
                      {heading === 'actions'
                        ? t('palette.sectionActions')
                        : t('palette.sectionNav')}
                    </p>
                  ) : null}

                  <button
                    type="button"
                    data-active={isActive}
                    onMouseMove={() => setActive(i)}
                    onClick={() => go(cmd)}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded px-2.5 py-2 text-left text-sm transition-colors',
                      isActive ? 'bg-primary-muted text-foreground' : 'text-foreground/80',
                    )}
                  >
                    <Icon
                      className={cn(
                        'h-4 w-4 shrink-0',
                        isActive
                          ? 'text-foreground'
                          : cmd.alarming
                            ? 'text-red-600'
                            : 'text-muted-foreground',
                      )}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 truncate">{cmd.label}</span>

                    {cmd.count ? (
                      <span
                        className={cn(
                          'tabular shrink-0 rounded px-1.5 py-0.5 text-2xs font-semibold ring-1',
                          cmd.alarming
                            ? 'bg-red-50 text-red-700 ring-red-200'
                            : 'bg-amber-50 text-amber-800 ring-amber-200',
                        )}
                      >
                        {cmd.count > 99 ? '99+' : cmd.count}
                      </span>
                    ) : null}

                    {isActive ? (
                      <CornerDownLeft
                        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                    ) : null}
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-center gap-4 border-t border-border bg-muted/40 px-4 py-2 text-2xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-border bg-background px-1 font-mono">↑↓</kbd>
            {t('palette.hintNavigate')}
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-border bg-background px-1 font-mono">↵</kbd>
            {t('palette.hintSelect')}
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-border bg-background px-1 font-mono">esc</kbd>
            {t('palette.hintClose')}
          </span>
        </div>
      </div>
    </div>
  );
}

/** The ⌘K / Ctrl+K listener. Split out so the shell owns the state and the trigger can share it. */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape') {
        // Also handled on the dialog itself, but that only hears the key while focus is INSIDE
        // it. Click anywhere else first and Escape went nowhere; this one always lands.
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return { open, setOpen };
}
